import { uniq } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarPersonWithLegalEntityId,
} from '~/components/EmployeeCalendar';
import { AbsenceStatus } from '~/data/absences/types';
import { useAllLegalEntities } from '~/data/legalEntity';
import { useGetPublicHolidays } from '~/services/Http/hooks';

import { SYSTEM_PUBLIC_HOLIDAY_EVENT } from './data';

export type UseEmployeeCalendarPublicHolidaysProps = {
  startDate: Date;
  endDate: Date;
  reportEmployees: EmployeeCalendarPersonWithLegalEntityId[];
};

export const useEmployeeCalendarPublicHolidays = (
  props: UseEmployeeCalendarPublicHolidaysProps,
) => {
  const { startDate, endDate, reportEmployees } = props;

  const { legalEntities } = useAllLegalEntities();

  const getPublicHolidaysReqParams = useMemo(() => {
    const legalEntityIds = legalEntities.map((le) => le.fLegalEntityId);

    return {
      legalEntities: legalEntityIds,
      countryCodes: undefined,
      years: uniq([startDate.getFullYear(), endDate.getFullYear()]),
    };
  }, [legalEntities, startDate, endDate]);

  const { data, ...rest } = useGetPublicHolidays(getPublicHolidaysReqParams);

  const publicHolidaysInRange = useMemo(() => {
    return data.filter(({ date }) =>
      moment(date).isBetween(startDate, endDate, 'day', '[]'),
    );
  }, [data, startDate, endDate]);

  // Process Public Holidays
  const { publicHolidayEvents } = useMemo(() => {
    const publicHolidayEvents: EmployeeCalendarEvent[] = reportEmployees
      .map(({ legalEntityId, ...employee }) => {
        return (
          publicHolidaysInRange
            ?.filter(
              (publicHoliday) => publicHoliday.legalEntityId === legalEntityId,
            )
            .map((publicHoliday, index): EmployeeCalendarEvent => {
              const { date } = publicHoliday;

              return {
                uId: `${employee.employeeId}-${moment(date).format(
                  'YYYYMMDD',
                )}-${index}`,
                ...employee,
                startDate: date,
                endDate: date,
                eventCategory: SYSTEM_PUBLIC_HOLIDAY_EVENT,
                eventCode: SYSTEM_PUBLIC_HOLIDAY_EVENT,
                status: AbsenceStatus.APPROVED,
              };
            }) ?? []
        );
      })
      .flat();

    return {
      publicHolidayEvents,
    };
  }, [reportEmployees, publicHolidaysInRange]);

  return {
    publicHolidayEvents,
    ...rest,
  };
};
